"use client";

import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronLeft,
  Mail,
  Phone,
  Globe,
  BookOpen,
  Calendar,
  Award,
  MapPin,
  Briefcase,
  GraduationCap,
  FileText,
  ExternalLink,
  Linkedin,
} from "lucide-react";

export default function FacultyDetailPage() {
  const [faculty, setFaculty] = useState(null);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();

  useEffect(() => {
    const fetchFacultyMember = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/faculty/faculty/${id}`
        );
        const data = await response.json();
        setFaculty(data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching faculty member:", error);
        setLoading(false);
      }
    };

    fetchFacultyMember();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!faculty) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Faculty Not Found
          </h2>
          <p className="text-gray-600 mb-4">
            The faculty member you're looking for doesn't exist or has been
            removed.
          </p>
          <Link
            to="/faculty"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ChevronLeft className="mr-2 h-5 w-5" />
            Back to Faculty List
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
      <div className="container mx-auto px-4 max-w-6xl">
        <Link to="/faculty" className="inline-block mb-8">
          <button className="flex items-center text-blue-600 hover:text-blue-800 transition-colors">
            <ChevronLeft className="mr-2 h-5 w-5" />
            Back to Faculty
          </button>
        </Link>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          <div className="md:flex">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="md:w-1/3 bg-gradient-to-br from-blue-600 to-blue-800 p-8 text-white"
            >
              <div className="sticky top-8">
                <div className="relative w-48 h-48 mx-auto mb-6">
                  <img
                    src={`${process.env.REACT_APP_API_URL}/faculty/${faculty.image_url}`}
                    alt={faculty.name}
                    className="rounded-full w-full h-full object-cover border-4 border-white shadow-lg"
                  />
                  <div className="absolute bottom-0 right-0 bg-green-500 rounded-full p-2">
                    <Award className="h-6 w-6 text-white" />
                  </div>
                </div>
                <h1 className="text-3xl font-bold mb-2 text-center">
                  {faculty.name}
                </h1>
                <p className="text-xl mb-4 text-center text-blue-100">
                  {faculty.role}
                </p>
                <div className="bg-white/10 rounded-lg p-4 mb-6">
                  <div className="flex items-center mb-2">
                    <Briefcase className="mr-2 h-5 w-5 text-blue-200" />
                    <p className="text-blue-100">{faculty.department}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <a
                    href={`mailto:${faculty.email}`}
                    className="flex items-center hover:bg-blue-700 p-2 rounded transition-colors"
                  >
                    <Mail className="mr-2 h-5 w-5" />
                    <p className="text-sm">{faculty.email}</p>
                  </a>
                  {/* <a
                    href={`tel:+12345678900`}
                    className="flex items-center hover:bg-blue-700 p-2 rounded transition-colors"
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    <p className="text-sm">+1 234 567 8900</p>
                  </a> */}

                  {faculty.linkedin_profile &&
                    faculty.linkedin_profile.trim() !== "" && (
                      <a
                        href={faculty.linkedin_profile}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center hover:bg-blue-700 p-2 rounded transition-colors"
                      >
                        <Linkedin className="mr-2 h-5 w-5" />
                        <p className="text-sm">LinkedIn Profile</p>
                      </a>
                    )}
                  {faculty.google_scholar &&
                    faculty.google_scholar.trim() !== "" && (
                      <a
                        href={faculty.google_scholar}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center hover:bg-blue-700 p-2 rounded transition-colors"
                      >
                        <Globe className="mr-2 h-5 w-5" />
                        <p className="text-sm">Google Scholar</p>
                      </a>
                    )}
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="md:w-2/3 p-8"
            >
              {faculty.bio && faculty.bio.trim() !== "" && (
                <section className="mb-12">
                  <h2 className="text-2xl font-bold mb-4 flex items-center text-gray-800">
                    <BookOpen className="mr-2 h-6 w-6 text-blue-600" />
                    Biography
                  </h2>
                  <p className="text-gray-600 leading-relaxed">{faculty.bio}</p>
                </section>
              )}

              <section className="mb-12">
                <h2 className="text-2xl font-bold mb-6 flex items-center text-gray-800">
                  <GraduationCap className="mr-2 h-6 w-6 text-blue-600" />
                  Education
                </h2>
                <div className="space-y-4">
                  {faculty.education.map((edu, index) => (
                    <div key={index} className="flex items-start">
                      <div className="w-2 h-2 bg-blue-600 rounded-full mr-3 mt-2"></div>
                      <div>
                        <h3 className="font-semibold text-gray-800">{edu}</h3>
                        <p className="text-gray-600">{edu.institution}</p>
                        <p className="text-sm text-gray-500">{edu.year}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {faculty.articles && faculty.articles.length > 0 && (
                <section className="mb-12">
                  <h2 className="text-2xl font-bold mb-6 flex items-center text-gray-800">
                    <FileText className="mr-2 h-6 w-6 text-blue-600" />
                    Top Publications
                  </h2>
                  <div className="space-y-4">
                    {faculty.articles.map((article, index) => (
                      <div
                        key={index}
                        className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
                      >
                        <a
                          href={article.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 font-medium flex items-start"
                        >
                          <div className="w-2 h-2 bg-blue-600 rounded-full mr-3 mt-2"></div>
                          <div>
                            <span className="mr-2">{article.title}</span>
                            <ExternalLink className="inline h-4 w-4" />
                            <p className="text-sm text-gray-500 mt-1">
                              {article.journal && article.year
                                ? `${article.journal}, ${article.year}`
                                : article.journal || article.year || ""}
                            </p>
                          </div>
                        </a>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Remove the separate Google Scholar section since it's now in the sidebar */}

              {/* <section>
                <h2 className="text-2xl font-bold mb-4 flex items-center text-gray-800">
                  <Award className="mr-2 h-6 w-6 text-blue-600" />
                  Awards and Honors
                </h2>
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
                  <ul className="space-y-2 text-gray-700">
                    {faculty.awards &&
                      faculty.awards.map((award, index) => (
                        <li key={index} className="flex items-center">
                          <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                          <span>
                            {award.title} - {award.year}
                          </span>
                        </li>
                      ))}
                  </ul>
                </div>
              </section> */}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
